import { TypedDocumentNode, gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { PlayerFieldStatus } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tag } from '@sorare/core/src/atoms/ui/Tag';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { range } from '@sorare/core/src/lib/arrays';
import { statusMessages } from '@sorare/core/src/lib/players';

import { SubIcon } from '@sorare/gameplay/src/components/stats/SubIcon';
import { getDecisiveActions } from '@sorare/gameplay/src/lib/so5';

import { PlayingStatusIcon } from 'components/rivals/PlayingStatusIcon';
import { RivalsAppearanceScore } from 'components/rivals/RivalsAppearanceScore';
import {
  RivalsPlayerCard,
  brightnessTransition,
} from 'components/rivals/match/RivalsPlayerCard';
import { GameSimulationAppearance } from 'components/rivals/matches/arena/ArenaLive/types';

import {
  Appearance_footballRivalsAppearance,
  Appearance_pollingFootballRivalsAppearance,
} from './__generated__/index.graphql';

const Root = styled.div`
  ${brightnessTransition}
`;
const CardAndScoreWrapper = styled(Vertical).attrs({ center: true })`
  justify-content: stretch;
`;
const PlayerCardWrapper = styled.div`
  position: relative;
`;
const Wrapper = styled(Horizontal).attrs({ gap: 0 })`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  & > * {
    margin-left: calc(-1 * var(--half-unit));
    margin-right: calc(-1 * var(--half-unit));
  }
`;
const DecisiveActionsWrapper = styled(Wrapper)`
  bottom: calc(-1 * var(--half-unit));
`;
const SubsWrapper = styled(Wrapper)`
  top: calc(-1 * var(--unit));
`;

const WaitingBadge = styled(Tag)`
  width: var(--quadruple-unit);
  background-color: var(--c-neutral-150);
`;

const LineupStatus = ({
  showStartingStatus,
  showPendingStatus,
  appearance,
  rarityBonusEnabled,
  noRandomDelay,
}: Pick<
  Props,
  | 'showStartingStatus'
  | 'showPendingStatus'
  | 'appearance'
  | 'rarityBonusEnabled'
> & { noRandomDelay?: boolean }) => {
  const { formatMessage } = useIntl();
  const { starting, brightness } = appearance;

  if (showStartingStatus) {
    return <PlayingStatusIcon isStarting={!!starting} />;
  }

  if (showPendingStatus) {
    return (
      <WaitingBadge>
        <FontAwesomeIcon
          icon={faClock}
          aria-label={formatMessage(statusMessages.PENDING)}
          color="var(--c-neutral-600)"
        />
      </WaitingBadge>
    );
  }
  return (
    <Root style={{ filter: `brightness(${brightness})` }}>
      <RivalsAppearanceScore
        hideReviewing
        appearance={appearance}
        rarityBonusEnabled={rarityBonusEnabled}
        animationDelayMs={noRandomDelay ? 0 : undefined}
      />
    </Root>
  );
};

type Props = {
  appearance: GameSimulationAppearance<Appearance_footballRivalsAppearance>;
  compact?: boolean;
  rarityBonusEnabled?: boolean;
  showStartingStatus?: boolean;
  showPendingStatus?: boolean;
  scoreNode?: ReactNode;
  simulation?: boolean;
};
export const Appearance = ({
  appearance,
  compact,
  rarityBonusEnabled,
  showStartingStatus,
  showPendingStatus,
  scoreNode,
  simulation,
}: Props) => {
  const { so5Score } = appearance;

  const decisiveActions = so5Score ? getDecisiveActions(so5Score) : null;

  const { playerGameStats } = so5Score || {};

  const substitutedOut =
    playerGameStats?.fieldStatus === PlayerFieldStatus.SUBSTITUTED;
  const substitutedIn =
    playerGameStats?.fieldStatus === PlayerFieldStatus.ON_FIELD &&
    !playerGameStats?.gameStarted;

  return (
    <CardAndScoreWrapper key={appearance.id}>
      <PlayerCardWrapper>
        <RivalsPlayerCard
          appearance={appearance}
          width={160}
          smallCaptain={compact}
        />
        <SubsWrapper>
          {substitutedOut && <SubIcon type="sub_out" />}
          {substitutedIn && <SubIcon type="sub_in" />}
        </SubsWrapper>
        <DecisiveActionsWrapper>
          {decisiveActions &&
            Object.entries(decisiveActions).map(([key, value]) =>
              range(value).map(i => (
                <StatIcon key={`${key}-${i}`} statKey={key} />
              ))
            )}
        </DecisiveActionsWrapper>
      </PlayerCardWrapper>
      {scoreNode || (
        <LineupStatus
          showStartingStatus={showStartingStatus}
          showPendingStatus={showPendingStatus}
          appearance={appearance}
          rarityBonusEnabled={rarityBonusEnabled || simulation}
          noRandomDelay={simulation}
        />
      )}
    </CardAndScoreWrapper>
  );
};

Appearance.pollingFragments = {
  footballRivalsAppearance: gql`
    fragment Appearance_pollingFootballRivalsAppearance on FootballRivalsAppearance {
      id
      score
      so5Score {
        id
        playerGameStats {
          id
          fieldStatus
          gameStarted
        }
        ...getDecisiveActions_playerGameScoreInterface
      }
    }
    ${getDecisiveActions.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<Appearance_pollingFootballRivalsAppearance>,
};

Appearance.fragments = {
  footballRivalsAppearance: gql`
    fragment Appearance_footballRivalsAppearance on FootballRivalsAppearance {
      id
      scoreWithoutRarityBonus
      starting
      ...RivalsPlayerCard_footballRivalsAppearance
      ...Appearance_pollingFootballRivalsAppearance
      ...RivalsAppearanceScore_footballRivalsAppearance
    }
    ${RivalsPlayerCard.fragments.footballRivalsAppearance}
    ${Appearance.pollingFragments.footballRivalsAppearance}
    ${RivalsAppearanceScore.fragments.footballRivalsAppearance}
  ` as TypedDocumentNode<Appearance_footballRivalsAppearance>,
};
